import { useMutation, useQuery } from "@tanstack/react-query";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import { backendService } from "../../../../../services/backend.service";
import type {
	ClipboardEnrollmentResponse,
	ClipboardRoleMappingApplyBody,
	ClipboardRoleMappingProposalResponse,
	ClipboardVerifyCredentialsResponse,
} from "./clipboardOnboardingTypes";

export const CLIPBOARD_ONBOARDING_QUERY_KEY = "clipboard-onboarding";

/**
 * Step 1: check the login and get the workplaces it manages, in one call. A mutation rather than a
 * query because it takes a secret in the body and must never be replayed, refetched on focus, or cached
 * — the credentials only live in component state for the length of the wizard.
 *
 * The password is never logged, tracked, or echoed back: analytics on this flow carry the department id
 * and the status discriminator only.
 */
export function useVerifyClipboardCredentials() {
	const { chosenDepartment } = useHospitalData();
	return useMutation<ClipboardVerifyCredentialsResponse, Error, { email: string; password: string }>({
		mutationFn: async (body) => (await backendService.verifyClipboardCredentials(chosenDepartment.department_id, body)).data,
		retry: false,
	});
}

/**
 * Step 3: the one committing call — writes the credential entry, then `clipboard_facility_id`.
 * `retry: false` because a retried enrollment is a second write against a live agency mapping, and
 * every rejection it can return (bad credentials, facility not managed, already mapped) is a settled
 * answer rather than a blip.
 *
 * `onEnrolled` is a MUTATION-level callback on purpose. The wizard is mounted conditionally, so closing
 * it while this POST is in flight unmounts the observer — and TanStack Query v5 only fires `mutate`-scoped
 * callbacks while that observer lives. A sync passed to `mutate` would therefore be silently dropped
 * exactly when it matters most: the server has committed, but `formData.clipboardFacilityId` and
 * `chosenDepartment` (which the post-to-agency gates read) would stay stale with no way back.
 */
export function useCreateClipboardEnrollment({ onEnrolled }: { onEnrolled: (facilityId: string) => void }) {
	const { chosenDepartment } = useHospitalData();
	return useMutation<ClipboardEnrollmentResponse, Error, { email: string; password: string; facilityId: string }>({
		mutationFn: async (body) => (await backendService.createClipboardEnrollment(chosenDepartment.department_id, body)).data,
		retry: false,
		onSuccess: (data) => onEnrolled(data.facilityId),
	});
}

/**
 * Step 4 (read): the syncer's own classifier dry-run over the facility's contracted worker types.
 *
 * Lazy and gated on `enabled` the same way the SFTP department-key picker is: the call reaches out to
 * Clipboard, so it waits until the wizard actually arrives at the mapping step rather than firing when
 * the dialog opens. `retry` is off — `credentials_missing` / `facility_not_managed` are answers, not blips.
 */
export function useClipboardRoleMappingProposal({ facilityId, enabled }: { facilityId: string | null; enabled: boolean }) {
	const { chosenDepartment } = useHospitalData();
	return useQuery<ClipboardRoleMappingProposalResponse>({
		queryKey: [CLIPBOARD_ONBOARDING_QUERY_KEY, "role-mapping-proposal", chosenDepartment.department_id, facilityId],
		queryFn: async () => {
			// Unreachable while `enabled` holds; thrown rather than coerced so a future caller that drops the
			// gate gets a real error instead of a request for the facility named "null".
			if (!facilityId) throw new Error("Clipboard role-mapping proposal requires a facility id");
			return (await backendService.getClipboardRoleMappingProposal(chosenDepartment.department_id, facilityId)).data;
		},
		enabled: enabled && !!facilityId,
		retry: false,
		staleTime: 60_000,
	});
}

/**
 * Step 4 (write): one bulk apply in one transaction. The response is the re-classified row set, so the
 * summary step can state the residual unmapped count from what the server actually sees.
 */
export function useApplyClipboardRoleMapping() {
	const { chosenDepartment } = useHospitalData();
	return useMutation<ClipboardRoleMappingProposalResponse, Error, ClipboardRoleMappingApplyBody>({
		mutationFn: async (body) => (await backendService.applyClipboardRoleMapping(chosenDepartment.department_id, body)).data,
		retry: false,
	});
}
